import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Task } from '../../../../types';
import {
  SignificationConfig,
  SignificationResponse,
  SignificationSubmission,
} from '../../../../types/signification.types';
import { CaptureOverlay } from '../shared';
import { TensionCapture } from './TensionCapture';
import { TriadCapture } from './TriadCapture';
import { StonesCapture } from './StonesCapture';
import { StimulusScreen } from './StimulusScreen';
import { JournalEntryScreen } from './JournalEntryScreen';
import { GuidedPractice } from './GuidedPractice';
import { SummaryScreen } from './SummaryScreen';
import './SignificationCapture.css';

export interface SignificationCaptureProps {
  taskName: string;
  prompt: string;
  onSubmit: (significationData: SignificationSubmission, audioBlobs: Blob[]) => void;
  onSaveDraft: () => void;
  onClose: () => void;
  task: Task;
  /** For journal mode: which entry number this is (1-based). Defaults to 1. */
  journalEntryNumber?: number;
}

export const SignificationCapture: React.FC<SignificationCaptureProps> = ({
  taskName,
  onSubmit,
  onClose,
  task,
  journalEntryNumber = 1,
}) => {
  const config = task.significationConfig;
  if (!config || config.items.length === 0) {
    return (
      <CaptureOverlay taskName={taskName} onClose={onClose}>
        <div className="sc-error">
          This task has no signification items configured.
        </div>
      </CaptureOverlay>
    );
  }

  return (
    <CaptureOverlay taskName={taskName} onClose={onClose}>
      <SignificationFlow config={config} onSubmit={onSubmit} onClose={onClose} journalEntryNumber={journalEntryNumber} />
    </CaptureOverlay>
  );
};

/* ── Inner flow component — manages item sequence ── */

interface FlowProps {
  config: SignificationConfig;
  onSubmit: (data: SignificationSubmission, audioBlobs: Blob[]) => void;
  onClose: () => void;
  journalEntryNumber: number;
}

const SignificationFlow: React.FC<FlowProps> = ({ config, onSubmit, onClose, journalEntryNumber }) => {
  const [guidedDone, setGuidedDone] = useState(!config.guidedMode);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showTransition, setShowTransition] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const responsesRef = useRef<SignificationResponse[]>([]);
  const audioBlobsRef = useRef<Blob[]>([]);

  // Pulse: show stimulus before signifiers
  const isPulse = config.collectionMode === 'pulse' && config.stimulus;
  const [stimulusDone, setStimulusDone] = useState(!isPulse);

  // Journal: show entry context before signifiers
  const isJournal = config.collectionMode === 'journal' && config.journalConfig;
  const [journalDone, setJournalDone] = useState(!isJournal);

  // Determine item order (fixed or randomized)
  const orderedItems = useMemo(() => {
    const items = [...config.items];
    if (config.order === 'randomized') {
      for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
      }
    }
    return items;
  }, [config]);

  const currentItem = orderedItems[currentIndex];
  const totalItems = orderedItems.length;

  const buildAndSubmit = useCallback(() => {
    const submission: SignificationSubmission = {
      configSnapshot: config,
      responses: responsesRef.current,
      completedAt: new Date().toISOString(),
      deviceInfo: {
        screenWidth: window.innerWidth,
        touchCapable: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        platform: /iPhone|iPad|iPod/.test(navigator.userAgent)
          ? 'ios'
          : /Android/.test(navigator.userAgent)
          ? 'android'
          : 'desktop',
      },
      ...(config.collectionMode === 'journal' ? { entryNumber: journalEntryNumber } : {}),
    };
    onSubmit(submission, audioBlobsRef.current);
  }, [config, onSubmit]);

  const handleItemComplete = useCallback((response: SignificationResponse, audioBlob: Blob | null) => {
    responsesRef.current = [...responsesRef.current, response];
    if (audioBlob) {
      audioBlobsRef.current = [...audioBlobsRef.current, audioBlob];
    }

    if (currentIndex < totalItems - 1) {
      // Show brief transition before next item
      if (totalItems > 1) {
        setShowTransition(true);
        setTimeout(() => {
          setShowTransition(false);
          setCurrentIndex(i => i + 1);
        }, 1500);
      } else {
        setCurrentIndex(i => i + 1);
      }
    } else {
      // All items done
      if (config.showSummary) {
        setShowSummary(true);
      } else {
        buildAndSubmit();
      }
    }
  }, [currentIndex, totalItems, config.showSummary, buildAndSubmit]);

  // Pulse stimulus screen (shown first)
  if (!stimulusDone) {
    return <StimulusScreen config={config} onContinue={() => setStimulusDone(true)} />;
  }

  // Journal entry context screen
  if (!journalDone) {
    return (
      <JournalEntryScreen
        config={config}
        entryNumber={journalEntryNumber}
        totalEntries={config.journalConfig?.totalEntries || 1}
        onContinue={() => setJournalDone(true)}
      />
    );
  }

  // Guided practice screen
  if (!guidedDone) {
    return <GuidedPractice onDone={() => setGuidedDone(true)} />;
  }

  // Transition between items
  if (showTransition) {
    return (
      <div className="sc-transition" onClick={() => {
        setShowTransition(false);
        setCurrentIndex(i => i + 1);
      }}>
        <div className="sc-transition-text">Now think about a different aspect of your experience.</div>
        <div className="sc-transition-hint">Tap to continue</div>
      </div>
    );
  }

  // Summary screen
  if (showSummary) {
    return (
      <SummaryScreen
        responses={responsesRef.current}
        items={orderedItems}
        onDone={buildAndSubmit}
      />
    );
  }

  if (!currentItem) return null;

  if (currentItem.mode === 'tension') {
    return (
      <TensionCapture
        key={currentItem.id}
        item={currentItem}
        onComplete={handleItemComplete}
        onClose={onClose}
        itemIndex={currentIndex}
        totalItems={totalItems}
      />
    );
  }

  if (currentItem.mode === 'stones') {
    return (
      <StonesCapture
        key={currentItem.id}
        item={currentItem}
        onComplete={handleItemComplete}
        onClose={onClose}
        itemIndex={currentIndex}
        totalItems={totalItems}
      />
    );
  }

  return (
    <TriadCapture
      key={currentItem.id}
      item={currentItem}
      onComplete={handleItemComplete}
      onClose={onClose}
      itemIndex={currentIndex}
      totalItems={totalItems}
    />
  );
};
